<template>
  <AdminLayout>
    <div class="max-w-6xl mx-auto">
      <!-- 헤더 -->
      <div class="flex items-center gap-3 mb-6">
        <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
          </svg>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-slate-800">주문 관리</h1>
          <p class="text-sm text-slate-500">포인트 상품 주문을 관리하고 기프티콘을 발송합니다</p>
        </div>
      </div>

      <!-- 상태 필터 -->
      <div class="flex gap-2 mb-6">
        <button
          v-for="filter in statusFilters"
          :key="filter.id"
          @click="selectedStatus = filter.id"
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium transition',
            selectedStatus === filter.id
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
        >
          {{ filter.label }}
          <span v-if="filter.count > 0" class="ml-1 px-1.5 py-0.5 text-xs bg-white/20 rounded-full">
            {{ filter.count }}
          </span>
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-12 h-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-slate-500">주문 불러오는 중...</p>
      </div>

      <!-- 빈 상태 -->
      <div v-else-if="filteredOrders.length === 0" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-slate-600 mb-2">주문이 없습니다</h3>
        <p class="text-slate-400">해당 상태의 주문이 없습니다.</p>
      </div>

      <!-- 주문 목록 -->
      <div v-else class="space-y-4">
        <div
          v-for="order in filteredOrders"
          :key="order.id"
          class="bg-white rounded-2xl p-5 shadow-sm"
        >
          <div class="flex items-start gap-4">
            <!-- 상품 이미지 (있으면) -->
            <div class="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <span class="text-2xl">🎁</span>
            </div>

            <!-- 주문 정보 -->
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between mb-2">
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <span :class="[
                      'px-2 py-0.5 text-xs font-semibold rounded-full',
                      getStatusStyle(order.status)
                    ]">
                      {{ getStatusLabel(order.status) }}
                    </span>
                    <span class="text-xs text-slate-400">
                      {{ formatDate(order.createdAt) }}
                    </span>
                  </div>
                  <h3 class="font-semibold text-slate-800">{{ order.productName }}</h3>
                  <p class="text-sm text-slate-500">{{ order.productBrand }}</p>
                </div>
                <div class="text-right">
                  <p class="font-bold text-rose-600">{{ order.price.toLocaleString() }}P</p>
                </div>
              </div>

              <!-- 고객 정보 -->
              <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mt-3 pt-3 border-t border-slate-100">
                <span class="flex items-center gap-1">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                  {{ order.userName || '이름 없음' }}
                </span>
                <span class="flex items-center gap-1">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                  {{ order.userEmail || '이메일 없음' }}
                </span>
                <span v-if="order.userPhone" class="flex items-center gap-1">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                  </svg>
                  {{ order.userPhone }}
                </span>
              </div>

              <!-- 기프티콘 정보 (완료된 경우) -->
              <div v-if="order.status === 'completed' && order.giftMessage" class="mt-3 p-3 bg-green-50 rounded-xl">
                <p class="text-sm font-medium text-green-700 mb-1">기프티콘 발송 완료</p>
                <p class="text-sm text-green-600">{{ order.giftMessage }}</p>
                <p class="text-xs text-green-500 mt-1">{{ formatDate(order.completedAt) }} 발송</p>
              </div>

              <!-- 액션 버튼 -->
              <div class="flex gap-2 mt-4">
                <button
                  v-if="order.status === 'pending'"
                  @click="openGiftModal(order)"
                  class="px-4 py-2 bg-rose-600 text-white text-sm font-medium rounded-lg hover:bg-rose-700 transition"
                >
                  기프티콘 발송
                </button>
                <button
                  v-if="order.status === 'pending'"
                  @click="handleCancel(order)"
                  class="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-100 rounded-lg transition"
                >
                  주문 취소 (환불)
                </button>
                <button
                  v-if="order.status === 'completed'"
                  @click="viewGiftDetail(order)"
                  class="px-4 py-2 text-blue-600 text-sm font-medium hover:bg-blue-50 rounded-lg transition"
                >
                  발송 내역 보기
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 기프티콘 발송 모달 -->
      <Teleport to="body">
        <Transition name="modal">
          <div v-if="showGiftModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div class="bg-white rounded-2xl p-6 w-full max-w-md" @click.stop>
              <h3 class="text-lg font-semibold text-slate-800 mb-4">기프티콘 발송</h3>

              <div class="mb-4 p-3 bg-slate-50 rounded-xl">
                <p class="text-sm text-slate-600">
                  <strong>{{ selectedOrder?.productName }}</strong>
                </p>
                <p class="text-sm text-slate-500">{{ selectedOrder?.userName }} 님에게 발송</p>
              </div>

              <form @submit.prevent="sendGift" class="space-y-4">
                <!-- 기프티콘 번호/메시지 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">기프티콘 번호 또는 메시지 *</label>
                  <textarea
                    v-model="giftForm.message"
                    rows="3"
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                    placeholder="기프티콘 번호, 사용 방법 등을 입력하세요"
                    required
                  ></textarea>
                </div>

                <!-- 기프티콘 이미지 (선택) -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">기프티콘 이미지 (선택)</label>
                  <div
                    @click="$refs.giftFileInput.click()"
                    class="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center cursor-pointer hover:border-rose-500 transition"
                  >
                    <div v-if="giftPreviewUrl" class="relative">
                      <img :src="giftPreviewUrl" class="w-full h-40 object-contain rounded-lg bg-slate-50"/>
                      <button
                        type="button"
                        @click.stop="clearGiftImage"
                        class="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        x
                      </button>
                    </div>
                    <div v-else class="py-4">
                      <svg class="w-10 h-10 mx-auto text-slate-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      <p class="text-sm text-slate-500">클릭하여 이미지 업로드</p>
                    </div>
                  </div>
                  <input
                    ref="giftFileInput"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handleGiftFileSelect"
                  />
                </div>

                <!-- 버튼 -->
                <div class="flex gap-2 pt-4">
                  <button
                    type="button"
                    @click="closeGiftModal"
                    class="flex-1 py-3 border border-slate-300 text-slate-600 rounded-xl hover:bg-slate-50 transition"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    :disabled="!giftForm.message || sendingGift"
                    class="flex-1 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition disabled:opacity-50"
                  >
                    {{ sendingGift ? '발송 중...' : '발송 완료 처리' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Transition>
      </Teleport>

      <!-- 발송 내역 보기 모달 -->
      <Teleport to="body">
        <Transition name="modal">
          <div v-if="showDetailModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div class="bg-white rounded-2xl p-6 w-full max-w-md" @click.stop>
              <h3 class="text-lg font-semibold text-slate-800 mb-4">발송 내역</h3>

              <div class="space-y-4">
                <div class="p-4 bg-slate-50 rounded-xl">
                  <p class="text-sm text-slate-500 mb-1">상품</p>
                  <p class="font-medium text-slate-800">{{ selectedOrder?.productName }}</p>
                </div>

                <div class="p-4 bg-slate-50 rounded-xl">
                  <p class="text-sm text-slate-500 mb-1">받는 사람</p>
                  <p class="font-medium text-slate-800">{{ selectedOrder?.userName }}</p>
                  <p class="text-sm text-slate-600">{{ selectedOrder?.userEmail }}</p>
                </div>

                <div class="p-4 bg-green-50 rounded-xl">
                  <p class="text-sm text-green-600 mb-1">기프티콘 정보</p>
                  <p class="font-medium text-green-800 whitespace-pre-wrap">{{ selectedOrder?.giftMessage }}</p>
                </div>

                <div v-if="selectedOrder?.giftImageUrl" class="p-4 bg-slate-50 rounded-xl">
                  <p class="text-sm text-slate-500 mb-2">기프티콘 이미지</p>
                  <img :src="selectedOrder.giftImageUrl" class="w-full rounded-lg"/>
                </div>

                <div class="text-sm text-slate-400 text-center">
                  {{ formatDate(selectedOrder?.completedAt) }} 발송 완료
                </div>
              </div>

              <button
                @click="showDetailModal = false"
                class="w-full mt-4 py-3 border border-slate-300 text-slate-600 rounded-xl hover:bg-slate-50 transition"
              >
                닫기
              </button>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { storage } from '@/firebase/firebase'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'
import { usePoint } from '@/composables/main/usePoint'

export default {
  name: 'OrderManageView',
  components: {
    AdminLayout
  },
  setup() {
    const { getAllOrders, completeOrder, cancelOrder } = usePoint()

    const loading = ref(true)
    const orders = ref([])
    const selectedStatus = ref('all')

    const showGiftModal = ref(false)
    const showDetailModal = ref(false)
    const selectedOrder = ref(null)
    const sendingGift = ref(false)

    const giftForm = ref({
      message: ''
    })
    const giftFile = ref(null)
    const giftPreviewUrl = ref('')

    const statusFilters = computed(() => {
      const pendingCount = orders.value.filter(o => o.status === 'pending').length
      const completedCount = orders.value.filter(o => o.status === 'completed').length
      const cancelledCount = orders.value.filter(o => o.status === 'cancelled').length

      return [
        { id: 'all', label: '전체', count: orders.value.length },
        { id: 'pending', label: '대기중', count: pendingCount },
        { id: 'completed', label: '발송완료', count: completedCount },
        { id: 'cancelled', label: '취소됨', count: cancelledCount }
      ]
    })

    const filteredOrders = computed(() => {
      if (selectedStatus.value === 'all') {
        return orders.value
      }
      return orders.value.filter(o => o.status === selectedStatus.value)
    })

    const getStatusLabel = (status) => {
      const labels = {
        pending: '대기중',
        completed: '발송완료',
        cancelled: '취소됨'
      }
      return labels[status] || status
    }

    const getStatusStyle = (status) => {
      const styles = {
        pending: 'bg-amber-100 text-amber-700',
        completed: 'bg-green-100 text-green-700',
        cancelled: 'bg-slate-100 text-slate-600'
      }
      return styles[status] || 'bg-slate-100 text-slate-600'
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    }

    const loadOrders = async () => {
      loading.value = true
      try {
        orders.value = await getAllOrders()
      } catch (err) {
        console.error('주문 로드 실패:', err)
      } finally {
        loading.value = false
      }
    }

    const openGiftModal = (order) => {
      selectedOrder.value = order
      giftForm.value = { message: '' }
      giftFile.value = null
      giftPreviewUrl.value = ''
      showGiftModal.value = true
    }

    const closeGiftModal = () => {
      showGiftModal.value = false
      selectedOrder.value = null
    }

    const handleGiftFileSelect = (e) => {
      const file = e.target.files[0]
      if (file) {
        giftFile.value = file
        giftPreviewUrl.value = URL.createObjectURL(file)
      }
    }

    const clearGiftImage = () => {
      giftFile.value = null
      giftPreviewUrl.value = ''
    }

    const uploadGiftImage = async (file) => {
      const fileName = `gift-images/${Date.now()}_${file.name}`
      const fileRef = storageRef(storage, fileName)
      await uploadBytes(fileRef, file)
      return getDownloadURL(fileRef)
    }

    const sendGift = async () => {
      if (!giftForm.value.message || !selectedOrder.value) return

      sendingGift.value = true
      try {
        let giftImageUrl = null
        if (giftFile.value) {
          giftImageUrl = await uploadGiftImage(giftFile.value)
        }

        await completeOrder(selectedOrder.value.id, giftForm.value.message, giftImageUrl)
        alert('기프티콘 발송이 완료되었습니다.')
        closeGiftModal()
        await loadOrders()
      } catch (err) {
        console.error('발송 실패:', err)
        alert('발송에 실패했습니다: ' + err.message)
      } finally {
        sendingGift.value = false
      }
    }

    const handleCancel = async (order) => {
      if (!confirm(`"${order.productName}" 주문을 취소하고 ${order.price.toLocaleString()}P를 환불하시겠습니까?`)) return

      try {
        await cancelOrder(order.id)
        alert('주문이 취소되고 포인트가 환불되었습니다.')
        await loadOrders()
      } catch (err) {
        console.error('취소 실패:', err)
        alert('취소에 실패했습니다: ' + err.message)
      }
    }

    const viewGiftDetail = (order) => {
      selectedOrder.value = order
      showDetailModal.value = true
    }

    onMounted(loadOrders)

    return {
      loading,
      orders,
      selectedStatus,
      statusFilters,
      filteredOrders,
      showGiftModal,
      showDetailModal,
      selectedOrder,
      sendingGift,
      giftForm,
      giftPreviewUrl,
      getStatusLabel,
      getStatusStyle,
      formatDate,
      openGiftModal,
      closeGiftModal,
      handleGiftFileSelect,
      clearGiftImage,
      sendGift,
      handleCancel,
      viewGiftDetail
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
